import type {
  BriefIssueResult,
  BriefView,
  DashboardSnapshot,
  DemoStatus,
  EnvName,
  ScenarioSummary,
  SectionForm,
  SimControlStatus,
  SimPlayStepResult,
  SubmitResponse,
  TierPreview,
  UiContext,
  VoicePlan,
  WeatherSyncResult,
  OperationAdvisory,
  BaseProfileView,
  CalendarOpsView,
  OperatorDirectory,
  OperatorView,
  OutageCreate,
  OutageUpdate,
  OutageView,
  RegulationLink,
  ReportSummary,
  RiskAcceptanceView,
  IngestFeedsPicture,
  NotamSyncResult,
} from "./api-types";

export type TransientCreateBody = {
  callsign?: string;
  aircraft_type: string;
  origin?: string;
  eta_zulu?: string;
  etd_zulu?: string;
  destination?: string;
  parking_spot?: string;
};

export type TransientPatchBody = Partial<TransientCreateBody>;

export type TransientRow = {
  id: string;
  date: string;
  callsign: string;
  aircraft: string;
  origin: string;
  eta: string;
  etd: string;
  dest: string;
  spot: string;
};

export type {
  BriefAck,
  BriefIssueResult,
  BriefSummary,
  BriefView,
  Conflict,
  DashboardSnapshot,
  DayCell,
  DemoStatus,
  EnvName,
  LabelValue,
  OperationalPicture,
  QuestionChoice,
  ScenarioSummary,
  SectionForm,
  SectionQuestion,
  SectionStatus,
  ShowWhen,
  SimControlStatus,
  SubmitResponse,
  TierPreview,
  UiContext,
  VoiceCommandWire,
  VoicePlan,
  WeatherSyncResult,
  OperationAdvisory,
  BaseProfileView,
  CalendarOpsView,
  OperatorDirectory,
  OperatorView,
  OutageCreate,
  OutageUpdate,
  OutageView,
  RegulationLink,
  ReportSummary,
  RiskAcceptanceView,
  IngestFeedsPicture,
  IngestFeedView,
  NotamSyncResult,
} from "./api-types";

export function apiBase(env: EnvName): string {
  return `/api/${env}/v1`;
}

/** LLM conflict scans during ingest/replay can take tens of seconds. */
const LLM_STEP_TIMEOUT_MS = 120_000;

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

function errorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== "object" || !("detail" in body)) return fallback;
  const detail = (body as { detail: unknown }).detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) =>
        item && typeof item === "object" && "msg" in item
          ? String((item as { msg: unknown }).msg)
          : null,
      )
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  return fallback;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!response.ok) {
    let message = response.statusText || "Request failed";
    if (text) {
      try {
        message = errorMessage(JSON.parse(text), message);
      } catch {
        message = text;
      }
    }
    throw new ApiError(message, response.status);
  }
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

export function showApiError(err: unknown): string | null {
  if (err == null || (err instanceof DOMException && err.name === "AbortError")) return null;
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.name !== "AbortError") {
    const msg = err.message.trim();
    return msg || null;
  }
  return null;
}

export function createApi(env: EnvName) {
  const base = apiBase(env);

  return {
    dashboard: () =>
      fetch(`${base}/assessments/dashboard`).then(parseJson<DashboardSnapshot>),
    sectionForm: (code: string) =>
      fetch(`${base}/sections/${code}/form`).then(parseJson<SectionForm>),
    previewTier: (
      code: string,
      answers: Record<string, string>,
      init?: RequestInit & { overrideTier?: string | null },
    ) => {
      const { overrideTier, ...fetchInit } = init ?? {};
      return fetch(`${base}/sections/${code}/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answers,
          override_tier: overrideTier ?? null,
        }),
        ...fetchInit,
      }).then(parseJson<TierPreview>);
    },
    submitSection: (
      sectionCode: string,
      answers: Record<string, string>,
      override_tier?: string,
      outlook_48h?: Record<string, string>,
      expected_revision?: string | null,
    ) =>
      fetch(`${base}/assessments/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          section_code: sectionCode,
          answers,
          override_tier,
          outlook_48h: outlook_48h ?? {},
          expected_revision: expected_revision ?? null,
        }),
      }).then(parseJson<SubmitResponse>),
    briefLatest: () =>
      fetch(`${base}/briefs/latest`).then(parseJson<BriefView>),
    createBrief: (notes: string) =>
      fetch(`${base}/briefs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notes }),
      }).then(parseJson<BriefIssueResult>),
    simStatus: () =>
      fetch(`${base}/control/status`).then(parseJson<SimControlStatus>),
    simRefresh: () =>
      fetch(`${base}/control/refresh-from-live`, { method: "POST" }).then(parseJson),
    resetBoard: () =>
      fetch(`${base}/control/reset`, { method: "POST" }).then(parseJson<DemoStatus>),
    simScenarios: () =>
      fetch(`${base}/control/scenarios`).then(parseJson<ScenarioSummary[]>),
    simLoadScenario: (path: string) =>
      fetch(`${base}/control/load-scenario`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path }),
        signal: AbortSignal.timeout(LLM_STEP_TIMEOUT_MS),
      }).then(parseJson<ScenarioSummary>),
    simPlayStep: (index: number, scenarioPath?: string) =>
      fetch(`${base}/control/play-step`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ index, scenario_path: scenarioPath ?? null }),
        signal: AbortSignal.timeout(LLM_STEP_TIMEOUT_MS),
      }).then(parseJson<SimPlayStepResult>),
    syncWeather: (icao = "KRCA") =>
      fetch(`${base}/ingest/sync?icao=${encodeURIComponent(icao)}`, {
        method: "POST",
      }).then(parseJson<WeatherSyncResult>),
    ingestFeeds: () =>
      fetch(`${base}/ingest/feeds`).then(parseJson<IngestFeedsPicture>),
    syncNotam: (icao = "KRCA", force = false) =>
      fetch(
        `${base}/ingest/notam/sync?icao=${encodeURIComponent(icao)}&force=${force ? "true" : "false"}`,
        { method: "POST" },
      ).then(parseJson<NotamSyncResult>),
    createTransient: (body: TransientCreateBody) =>
      fetch(`${base}/transients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(parseJson<TransientRow>),
    updateTransient: (id: string, body: TransientPatchBody) =>
      fetch(`${base}/transients/${encodeURIComponent(id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(parseJson<TransientRow>),
    deleteTransient: (id: string) =>
      fetch(`${base}/transients/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }).then(parseJson<void>),
    clearTransients: (filters?: { aircraft_type?: string; callsign?: string }) => {
      const params = new URLSearchParams();
      if (filters?.aircraft_type) params.set("aircraft_type", filters.aircraft_type);
      if (filters?.callsign) params.set("callsign", filters.callsign);
      const qs = params.toString();
      return fetch(`${base}/transients${qs ? `?${qs}` : ""}`, {
        method: "DELETE",
      }).then(parseJson<{ removed: number }>);
    },
    voiceCommand: (query: string, context?: UiContext | null, sessionId?: string | null) =>
      fetch(`${base}/voice/command`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query,
          context: context ?? undefined,
          session_id: sessionId ?? undefined,
        }),
      }).then(parseJson<VoicePlan>),
    voiceReady: () => fetch(`${base}/voice/ready`).then(parseJson<{ ready: boolean }>),
    advisoryLatest: () =>
      fetch(`${base}/advisory/latest`).then(parseJson<OperationAdvisory>),
    advisoryReady: () => fetch(`${base}/advisory/ready`).then(parseJson<{ ready: boolean }>),
    advisoryRefresh: () =>
      fetch(`${base}/advisory/refresh`, { method: "POST" }).then(parseJson<OperationAdvisory>),
    shiftsReady: () =>
      fetch(`${base}/shifts/ready`).then(parseJson<{ ready: boolean }>),
    sectionShifts: (code: string) =>
      fetch(`${base}/sections/${code}/shifts`).then(parseJson),
    startShift: (code: string, operatorInitials?: string | null) =>
      fetch(`${base}/sections/${code}/shifts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ operator_initials: operatorInitials ?? null }),
      }).then(parseJson),
    getHandover: (shiftId: string) =>
      fetch(`${base}/shifts/${shiftId}/handover`).then(parseJson),
    generateHandover: (shiftId: string) =>
      fetch(`${base}/shifts/${shiftId}/handover`, {
        method: "POST",
        signal: AbortSignal.timeout(LLM_STEP_TIMEOUT_MS),
      }).then(parseJson),
    outages: () => fetch(`${base}/operations/outages`).then(parseJson<OutageView[]>),
    createOutage: (body: OutageCreate) =>
      fetch(`${base}/operations/outages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(parseJson<OutageView>),
    updateOutage: ({ id, ...body }: OutageUpdate & { id: string }) =>
      fetch(`${base}/operations/outages/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(parseJson<OutageView>),
    calendarOps: () => fetch(`${base}/operations/calendar`).then(parseJson<CalendarOpsView>),
    createCalendarEvent: (body: {
      name: string;
      start_date: string;
      end_date?: string | null;
      event_type?: string;
      color?: string | null;
      summary?: string | null;
    }) =>
      fetch(`${base}/operations/calendar/events`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(parseJson),
    createHoursRequest: (body: Record<string, string | null>) =>
      fetch(`${base}/operations/calendar/hours-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(parseJson),
    acceptances: () =>
      fetch(`${base}/operations/acceptances`).then(parseJson<RiskAcceptanceView[]>),
    createAcceptance: (body: {
      risk_label: string;
      accepted_by: string;
      role: string;
      rationale: string;
      controls?: string | null;
    }) =>
      fetch(`${base}/operations/acceptances`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(parseJson<RiskAcceptanceView>),
    operators: () =>
      fetch(`${base}/operations/operators`).then(parseJson<OperatorDirectory>),
    createOperator: (body: {
      display_name: string;
      initials?: string | null;
      roles: string[];
      sections: string[];
    }) =>
      fetch(`${base}/operations/operators`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }).then(parseJson<OperatorView>),
    baseProfile: () =>
      fetch(`${base}/operations/base-profile`).then(parseJson<BaseProfileView>),
    regulations: () =>
      fetch(`${base}/operations/regulations`).then(parseJson<RegulationLink[]>),
    report: () => fetch(`${base}/operations/report`).then(parseJson<ReportSummary>),
  };
}
